'use strict';

const buildCTAEvaluator = require('./buildCTAEvaluator');
const { saveExecutionGate } = require('../coordinator/saveExecutionGate');

/**
 * Shared configurator CTA selectors (Order Now, Save to CXP, Summary / Next).
 *
 * `detail.js` loads before `configurator.js`, so these lists live here instead of
 * on `window.configurator`.
 */
export const configuratorCtas = {
  /**
   * Order Now CTAs gated by dealer `onlineOrderingEnabled`. Always in DOM; visibility
   * and `data-online-ordering` sync via Variation. Includes `#configuratorOrder-tab`.
   *
   * @readonly
   */
  ONLINE_ORDERING: [
    '#configurator-footer-actions-checkout',
    '#configurator-footer-actions-checkout-m',
    '#summary-footer-actions-checkout-m',
    '#configuratorOrder-tab',
    '.configurator-navigation .btn-order-online'
  ].join(', '),

  /**
   * Navbar consumer Order Online. Visibility only; never `disabled`.
   *
   * @readonly
   */
  NAV_ORDER_ONLINE: '.configurator-navigation .btn-order-online',

  /**
   * Footer Save-to-CXP hook. Stock / `allowConfigurator` writers use this, not nav Save
   * (`.btn-save-to-cxp` is owned by `saveToCxpGate`).
   *
   * @readonly
   */
  CXP_HOOK: '.configurator-send-offine-order-cta',

  /**
   * Save to CXP CTAs (footer hook + navbar). Hide/show and CXP POST, not stock enable.
   *
   * @readonly
   */
  CXP: [
    '.configurator-send-offine-order-cta',
    '.btn-save-to-cxp'
  ].join(', '),

  /**
   * Summary / Next CTAs gated by `showBuildCTA` (variant flags). Footer hook + navbar + flyout.
   *
   * @readonly
   */
  BUILD: [
    '#configurator-footer-actions-summary',
    '#configurator-footer-actions-summary-m',
    '.configurator-navigation .btn-summary',
    '.configurator-navigation .btn-next',
    '.list-group-item-action[data-view="order-online"]'
  ].join(', '),

  /**
   * Hidden Order tab — `disabled` is stage-gated (Summary), not availability.
   *
   * @readonly
   */
  ORDER_TAB: '#configuratorOrder-tab',

  /**
   * Order Now CTAs excluding the stage-gated Order tab.
   *
   * @returns {JQuery}
   */
  $orderNow() {
    return $(this.ONLINE_ORDERING).not(this.ORDER_TAB).not(this.NAV_ORDER_ONLINE);
  },

  /**
   * Availability-gated CTAs (Order Now + footer CXP hook), excluding the Order tab.
   * Nav Save is omitted so Variation / `allowConfigurator` cannot enable it before basket sync.
   *
   * @returns {JQuery}
   */
  $availability() {
    return this.$orderNow();
  },

  /**
   * Soft-block disable set when `allowConfigurator` is false (Order Now + footer CXP hook).
   * Nav Save stays with `saveToCxpGate`.
   *
   * @returns {JQuery}
   */
  $configuratorAccess() {
    return this.$orderNow().add(this.CXP_HOOK);
  },

  /**
   * Variation-written `data-isProductOrderable` from the first Order Now CTA that has it
   * (nav or leftover `-m` still work when the footer hook is missing).
   *
   * @returns {boolean}
   */
  isProductOrderable() {
    var $ctas = this.$orderNow();
    var i;
    var value;

    for (i = 0; i < $ctas.length; i += 1) {
      value = $($ctas[i]).data('isProductOrderable');

      if (value === true) {
        return true;
      }

      if (value === false) {
        return false;
      }
    }

    return false;
  },

  /**
   * Whether any Order Now CTA already has Variation-written `data-isProductOrderable`.
   *
   * @returns {boolean}
   */
  hasProductOrderableFlag() {
    var $ctas = this.$orderNow();
    var i;
    var value;

    for (i = 0; i < $ctas.length; i += 1) {
      value = $($ctas[i]).data('isProductOrderable');

      if (value === true || value === false) {
        return true;
      }
    }

    return false;
  },

  /**
   * Enable-writer for the footer Save-to-CXP hook.
   *
   * Does not read `allowConfigurator` — that is a disable-writer
   * (`applyConfiguratorDecision` / `reapplyOrderNowDisabled`) applied after this.
   * Skips terminal success and peer-tab lease.
   */
  syncCxpHookEnabled() {
    var $hook = $(this.CXP_HOOK);

    if (!$hook.length || $hook.hasClass('cxp-success')) {
      return;
    }

    if ($hook.attr(saveExecutionGate.SAVE_GATE_PEER_ATTR) === '1') {
      return;
    }

    $hook.removeClass('disabled').prop('disabled', false);
  },

  /**
   * Re-apply stock / DAP / `allowConfigurator` after a writer clears `disabled`
   * (`detectTabMgr`, DAP success, ATC). Does not enable CTAs. No-op until Variation
   * has written `data-isProductOrderable`.
   */
  reapplyOrderNowDisabled() {
    var $orderNow;
    var flags;

    if (!this.hasProductOrderableFlag()) {
      return;
    }

    $orderNow = this.$orderNow();
    flags = buildCTAEvaluator.getFlags();

    if (!this.isProductOrderable()) {
      $orderNow.addClass('disabled').prop('disabled', true);
    }

    if ($('#configurator-dap-not-available').hasClass('d-flex')) {
      $orderNow.addClass('disabled');
    }

    if (flags && flags.allowConfigurator === false) {
      this.$configuratorAccess().addClass('disabled');
    }

    if (window.configurator && typeof window.configurator.syncSummaryNavConsumerCtas === 'function') {
      window.configurator.syncSummaryNavConsumerCtas();
    }
  }
};
